
import React from 'react';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NoteComment } from '@/types/note';
import mindnoteBotLogoWhite from '@/assets/mindnote-bot-logo-white.png';
import { useAIHighlightPreference } from '@/hooks/useAIHighlightPreference';
import BlockNoteEditorWrapper from './BlockNoteEditor';


interface NoteContentProps {
  title: string;
  content: string;
  externalRevision?: number;
  onTitleChange?: (value: string) => void;
  onContentChange?: (value: string) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  onTextSelection?: () => void;
  backgroundColor: string;
  comments?: NoteComment[];
  isWysiwygMode?: boolean;
  onGetCurrentElement?: (element: HTMLTextAreaElement | HTMLDivElement | null) => void;
  onOpenCommentForText?: (start: number, end: number, commentId: string) => void;
}

const NoteContent: React.FC<NoteContentProps> = ({
  title,
  content,
  externalRevision = 0,
  onTitleChange,
  onContentChange,
  textareaRef,
  onTextSelection,
  backgroundColor,
  comments = [],
  isWysiwygMode = false,
  onGetCurrentElement,
  onOpenCommentForText,
}) => {
  const isEditable = !!onTitleChange && !!onContentChange;
  const [showAIHighlights] = useAIHighlightPreference();
  const contentEditableRef = React.useRef<HTMLDivElement>(null);
  const savedSelectionRef = React.useRef<{ start: number; end: number } | null>(null);
  // Track markdown last emitted from the editor so we can skip re-rendering the
  // contentEditable on the user's own keystrokes (which would collapse spaces,
  // reset caret, and cause unwanted formatting changes).
  const lastEmittedMarkdownRef = React.useRef<string | null>(null);
  const isUserTypingRef = React.useRef(false);

  const saveCurrentSelection = () => {
    const div = contentEditableRef.current;
    if (!div) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(div);
    preRange.setEnd(range.startContainer, range.startOffset);
    const start = preRange.toString().length;
    const end = start + range.toString().length;
    savedSelectionRef.current = { start, end };
  };
  // Expose the current active element to parent.
  // In WYSIWYG mode the BlockNote wrapper supplies its own element via
  // onElementRef → onGetCurrentElement, so do NOT overwrite it here with
  // the (unused) legacy contentEditableRef, which would be null and wipe
  // out the live selection target used by the comment button.
  React.useEffect(() => {
    if (!onGetCurrentElement) return;
    if (isWysiwygMode) return;
    onGetCurrentElement(textareaRef?.current || null);
  }, [isWysiwygMode, onGetCurrentElement, textareaRef]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTitleChange?.(e.target.value);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onContentChange?.(e.target.value);
  };

  const handleWysiwygContentChange = (e: React.FormEvent<HTMLDivElement>) => {
    const div = e.currentTarget;
    saveCurrentSelection();
    // Extract plain text content while preserving structure
    const extractMarkdownFromDiv = (element: HTMLElement): string => {
      let markdown = '';
      
      for (const node of Array.from(element.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
          markdown += node.textContent || '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const elem = node as HTMLElement;
          
          switch (elem.tagName.toLowerCase()) {
            case 'h1':
              markdown += `<h1>${extractMarkdownFromDiv(elem)}</h1>\n`;
              break;
            case 'h2':
              markdown += `<h2>${extractMarkdownFromDiv(elem)}</h2>\n`;
              break;
            case 'h3':
              markdown += `<h3>${extractMarkdownFromDiv(elem)}</h3>\n`;
              break;
            case 'h4':
              markdown += `<h4>${extractMarkdownFromDiv(elem)}</h4>\n`;
              break;
            case 'h5':
              markdown += `<h5>${extractMarkdownFromDiv(elem)}</h5>\n`;
              break;
            case 'h6':
              markdown += `<h6>${extractMarkdownFromDiv(elem)}</h6>\n`;
              break;
            case 'strong':
            case 'b':
              markdown += `**${extractMarkdownFromDiv(elem)}**`;
              break;
            case 'em':
            case 'i':
              markdown += `*${extractMarkdownFromDiv(elem)}*`;
              break;
            case 'u':
              markdown += `<u>${extractMarkdownFromDiv(elem)}</u>`;
              break;
            case 'del':
              markdown += `~~${extractMarkdownFromDiv(elem)}~~`;
              break;
            case 'code':
              markdown += `\`${elem.textContent}\``;
              break;
            case 'a':
              const href = elem.getAttribute('href');
              markdown += `[${elem.textContent}](${href})`;
              break;
            case 'blockquote':
              markdown += `> ${elem.textContent}\n`;
              break;
            case 'ul':
            case 'ol': {
              const isOrdered = elem.tagName.toLowerCase() === 'ol';
              const lis = Array.from(elem.children).filter(
                (c) => c.tagName.toLowerCase() === 'li'
              ) as HTMLElement[];
              lis.forEach((li, index) => {
                const checkbox = li.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
                // Use label>span as content source for task items so the
                // checkbox/label chrome doesn't leak into the markdown.
                const contentEl = (li.querySelector(':scope > label > span') as HTMLElement | null) || li;
                // Recurse so inline formatting (bold/italic/color/font/heading)
                // inside the list item is preserved through the roundtrip.
                const inner = extractMarkdownFromDiv(contentEl).replace(/\n+$/g, '');
                if (checkbox) {
                  const isChecked = checkbox.hasAttribute('checked') || checkbox.checked;
                  markdown += `- [${isChecked ? 'x' : ' '}] ${inner}\n`;
                } else if (isOrdered) {
                  markdown += `${index + 1}. ${inner}\n`;
                } else {
                  markdown += `- ${inner}\n`;
                }
              });
              break;
            }
            case 'br':
              markdown += '\n';
              break;
            case 'div':
            case 'p': {
              const divContent = extractMarkdownFromDiv(elem);
              // Preserve the block exactly as typed. Do not trim here: mobile
              // WebViews can otherwise lose intentional spaces when WYSIWYG
              // formatting dispatches an input event after bold/italic/etc.
              if (divContent.length > 0 || elem.querySelector('br')) {
                // Avoid double newlines when a block already ends with one
                // (e.g. nested <div><div>...</div></div> from browser Enter,
                // or a heading wrapper inside the block). This is what was
                // injecting an "extra space" after combined formatting.
                markdown += divContent.endsWith('\n') ? divContent : divContent + '\n';
              }
              break;
            }
            case 'span':
              const color = elem.style.color;
              // Sanitize font family: take first family and strip wrapping quotes
              const rawFontFamily = (elem.style as any).fontFamily as string | undefined;
              const cleanedFontFamily = rawFontFamily
                ? rawFontFamily.split(',')[0].trim().replace(/^['"]|['"]$/g, '')
                : '';
              if (color && cleanedFontFamily) {
                markdown += `<font face="${cleanedFontFamily}"><span style="color: ${color};">${extractMarkdownFromDiv(elem)}</span></font>`;
              } else if (color) {
                markdown += `<span style="color: ${color};">${extractMarkdownFromDiv(elem)}</span>`;
              } else if (cleanedFontFamily) {
                markdown += `<font face="${cleanedFontFamily}">${extractMarkdownFromDiv(elem)}</font>`;
              } else {
                // Handle nested formatting within span
                const spanContent = extractMarkdownFromDiv(elem);
                markdown += spanContent || elem.textContent || '';
              }
              break;
            case 'font':
              const face = elem.getAttribute('face') || '';
              if (face) {
                markdown += `<font face="${face}">${extractMarkdownFromDiv(elem)}</font>`;
              } else {
                markdown += extractMarkdownFromDiv(elem);
              }
              break;
            default:
              markdown += elem.textContent || '';
          }
        }
      }
      
      return markdown;
    };
    
    const markdown = extractMarkdownFromDiv(div);
    // Normalize: drop a single trailing newline + collapse 3+ consecutive
    // newlines down to 2 (one blank line). Prevents combined formatting
    // (bold + heading + list, etc.) from accumulating phantom blank lines
    // every time the input event re-renders.
    let cleanedMarkdown = markdown.endsWith('\n') ? markdown.slice(0, -1) : markdown;
    cleanedMarkdown = cleanedMarkdown.replace(/\n{3,}/g, '\n\n');
    isUserTypingRef.current = true;
    lastEmittedMarkdownRef.current = cleanedMarkdown;
    onContentChange?.(cleanedMarkdown);
  };

  const handleCopy = (e: React.ClipboardEvent) => {
    if (isWysiwygMode && contentEditableRef.current) {
      // WYSIWYG mode: copy as rich text without HTML tags
      e.preventDefault();
      
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Create a temporary div to process the selected content
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(range.cloneContents());
        
        // Convert HTML to plain text while preserving formatting structure
        const convertToPlainText = (element: HTMLElement): string => {
          let text = '';
          
          for (const node of Array.from(element.childNodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
              text += node.textContent || '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const elem = node as HTMLElement;
              
              switch (elem.tagName.toLowerCase()) {
                case 'h1':
                case 'h2':
                case 'h3':
                case 'h4':
                case 'h5':
                case 'h6':
                  text += elem.textContent + '\n';
                  break;
                case 'p':
                case 'div':
                  text += elem.textContent + '\n';
                  break;
                case 'br':
                  text += '\n';
                  break;
                case 'ul':
                case 'ol':
                  // Process list items
                  for (const li of elem.querySelectorAll('li')) {
                    const checkbox = li.querySelector('input[type="checkbox"]') as HTMLInputElement;
                    if (checkbox) {
                      const isChecked = checkbox.checked;
                      text += `- [${isChecked ? 'x' : ' '}] ${li.textContent?.replace(/^\s*/, '')}\n`;
                    } else if (elem.tagName.toLowerCase() === 'ol') {
                      text += `1. ${li.textContent}\n`;
                    } else {
                      text += `- ${li.textContent}\n`;
                    }
                  }
                  break;
                case 'blockquote':
                  text += `> ${elem.textContent}\n`;
                  break;
                default:
                  text += elem.textContent || '';
              }
            }
          }
          
          return text;
        };
        
        const plainText = convertToPlainText(tempDiv);
        
        // Use the Clipboard API for better browser support
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(plainText).catch(() => {
            // Fallback to older method
            const textArea = document.createElement('textarea');
            textArea.value = plainText;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
          });
        } else {
          // Fallback method
          const textArea = document.createElement('textarea');
          textArea.value = plainText;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      }
    }
    // HTML mode: let browser handle default copy (copies raw HTML/markdown as is)
  };

  const handleTextSelection = () => {
    if (isWysiwygMode && contentEditableRef.current) {
      // Handle selection in contentEditable div
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const div = contentEditableRef.current;
        
        // Convert DOM range to text content positions
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(div);
        preCaretRange.setEnd(range.startContainer, range.startOffset);
        const startOffset = preCaretRange.toString().length;
        
        const selectedText = range.toString();
        const endOffset = startOffset + selectedText.length;
        
        console.log('WYSIWYG selection:', { startOffset, endOffset, selectedText });
        
        // Store selection info reliably
        (div as any).selectionStart = startOffset;
        (div as any).selectionEnd = endOffset;
        (div as any).selectedText = selectedText;
        (div as any).value = div.textContent || '';
      }
    }
    onTextSelection?.();
  };

  const getHighlightColor = (index: number) => {
    const colors = [
      '#F2FCE2',  // Soft Green
      '#FEF7CD',  // Soft Yellow
      '#FEC6A1',  // Soft Orange
      '#E5DEFF',  // Soft Purple
      '#FFDEE2',  // Soft Pink
      '#FDE1D3',  // Soft Peach
      '#D3E4FD',  // Soft Blue
      '#F1F0FB'   // Soft Gray
    ];
    return colors[index % colors.length];
  };

  const renderHighlightedContent = () => {
    // First, process AI-generated content markers - use greedy match to capture everything until the last closing tag
    let processedContent = content.replace(
      /<ai-generated>((?:(?!<\/ai-generated>)[\s\S])*)<\/ai-generated>/g,
      showAIHighlights
        ? '<span class="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-1 rounded-sm border-l-2 border-blue-400 dark:border-blue-500">$1</span>'
        : '$1'
    );
    // Strip any stray/unmatched ai-generated tags so they never render literally
    processedContent = processedContent.replace(/<\/?ai-generated>/g, '');
    
    let lastIndex = 0;
    const result = [];
    const sortedComments = [...comments].sort((a, b) => a.selection.start - b.selection.start);

    sortedComments.forEach((comment, index) => {
      if (comment.selection.start > lastIndex) {
        const normalText = processedContent.slice(lastIndex, comment.selection.start);
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let lastLinkIndex = 0;
        let match;
        
        while ((match = linkRegex.exec(normalText)) !== null) {
          if (match.index > lastLinkIndex) {
            result.push(normalText.slice(lastLinkIndex, match.index));
          }
          result.push(`<a href="${match[2]}" class="text-blue-600">${match[1]}</a>`);
          lastLinkIndex = match.index + match[0].length;
        }
        if (lastLinkIndex < normalText.length) {
          result.push(normalText.slice(lastLinkIndex));
        }
      }

      const highlightedText = processedContent.slice(comment.selection.start, comment.selection.end);
      result.push(
        `<span style="background-color: rgba(250, 204, 21, 0.2); border-bottom: 2px solid rgba(250, 204, 21, 0.5); cursor: pointer; position: relative;" title="💬 Comment: ${comment.text}" data-comment-id="${comment.id}" data-selection-start="${comment.selection.start}" data-selection-end="${comment.selection.end}" onclick="window.openCommentForText && window.openCommentForText(${comment.selection.start}, ${comment.selection.end}, '${comment.id}')">${highlightedText}</span>`
      );

      lastIndex = comment.selection.end;
    });

    if (lastIndex < processedContent.length) {
      const remainingText = processedContent.slice(lastIndex);
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      let lastLinkIndex = 0;
      let match;
      
      while ((match = linkRegex.exec(remainingText)) !== null) {
        if (match.index > lastLinkIndex) {
          result.push(remainingText.slice(lastLinkIndex, match.index));
        }
        result.push(`<a href="${match[2]}" class="text-blue-600">${match[1]}</a>`);
        lastLinkIndex = match.index + match[0].length;
      }
      
      if (lastLinkIndex < remainingText.length) {
        result.push(remainingText.slice(lastLinkIndex));
      }
    }

    return result.join('');
  };

  const handleTaskToggle = (checkbox: HTMLInputElement) => {
    const taskText = checkbox.getAttribute('data-task-text') || '';
    const isChecked = checkbox.checked;
    const span = checkbox.parentNode?.querySelector('span');
    
    // Immediate visual feedback
    if (span) {
      if (isChecked) {
        span.style.textDecoration = 'line-through';
        span.style.opacity = '0.6';
      } else {
        span.style.textDecoration = 'none';
        span.style.opacity = '1';
      }
    }

    // Update content if editing is allowed
    if (onContentChange && typeof onContentChange === 'function') {
      try {
        const escapedTaskText = taskText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const uncheckedPattern = new RegExp(`^\\s*- \\[ \\] ${escapedTaskText}\\s*$`, 'm');
        const checkedPattern = new RegExp(`^\\s*- \\[x\\] ${escapedTaskText}\\s*$`, 'm');
        let newContent = content;

        if (isChecked) {
          newContent = content.replace(uncheckedPattern, `- [x] ${taskText}`);
        } else {
          newContent = content.replace(checkedPattern, `- [ ] ${taskText}`);
        }
        onContentChange(newContent);
      } catch (error) {
        console.warn('Failed to update task content:', error);
      }
    }
  };

  const selectCommentText = (start: number, end: number) => {
    if (isWysiwygMode) {
      // In WYSIWYG (BlockNote) mode the legacy contentEditableRef is unused,
      // so fall back to the live BlockNote container in the DOM.
      const div: HTMLElement | null =
        contentEditableRef.current ||
        (document.querySelector('.bn-note-surface') as HTMLElement | null);
      if (!div) return;
      const selection = window.getSelection();
      if (!selection) return;

      try {
        const range = document.createRange();
        let current = 0;
        let startSet = false;
        let endSet = false;

        const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT, null);
        let node = walker.nextNode() as Text | null;

        while (node && (!startSet || !endSet)) {
          const nodeText = node.textContent || '';
          const nodeLength = nodeText.length;
          if (nodeLength === 0) {
            node = walker.nextNode() as Text | null;
            continue;
          }
          const nodeEnd = current + nodeLength;
          if (!startSet && start >= current && start <= nodeEnd) {
            range.setStart(node, Math.min(start - current, nodeLength));
            startSet = true;
          }
          if (!endSet && end >= current && end <= nodeEnd) {
            range.setEnd(node, Math.min(end - current, nodeLength));
            endSet = true;
            break;
          }
          current = nodeEnd;
          node = walker.nextNode() as Text | null;
        }

        if (startSet && endSet) {
          selection.removeAllRanges();
          selection.addRange(range);
          (div as HTMLElement).focus?.();
          // Scroll the selection into view so the user can see where the
          // commented text lives in the note.
          try {
            const rect = range.getBoundingClientRect();
            if (rect && rect.height > 0) {
              const target = (range.startContainer.parentElement as HTMLElement) || div;
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } catch {}
        }
      } catch (error) {
        console.warn('Error selecting comment text in WYSIWYG mode:', error);
      }
    } else if (textareaRef?.current) {
      const textarea = textareaRef.current;
      textarea.focus();
      textarea.setSelectionRange(start, end);
    }
  };

  // Make the function globally available for the HTML onclick handlers
  React.useEffect(() => {
    (window as any).handleTaskToggle = handleTaskToggle;
    (window as any).selectCommentText = selectCommentText;
    (window as any).openCommentForText = (start: number, end: number, commentId: string) => {
      if (onOpenCommentForText) {
        onOpenCommentForText(start, end, commentId);
      }
    };

    // Delegate clicks/changes to handle toggling inside WYSIWYG/read-only views
    const handleDelegated = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      let checkbox: HTMLInputElement | null = null;
      if (target instanceof HTMLInputElement && target.type === 'checkbox' && target.hasAttribute('data-task-text')) {
        checkbox = target;
      } else {
        const fromLabel = target.closest('label')?.querySelector('input[type="checkbox"][data-task-text]') as HTMLInputElement | null;
        const fromSelf = target.closest('input[type="checkbox"][data-task-text]') as HTMLInputElement | null;
        checkbox = fromLabel || fromSelf;
      }

      if (checkbox) {
        // Use change/input for reliable checked state; click is just a fallback trigger
        if (e.type === 'change' || e.type === 'input') {
          handleTaskToggle(checkbox);
        } else if (e.type === 'click' && !(target instanceof HTMLInputElement)) {
          // If clicked non-input (like span), defer to next tick to ensure browser toggled
          setTimeout(() => handleTaskToggle(checkbox!), 0);
        }
      }
    };

    document.addEventListener('input', handleDelegated);
    document.addEventListener('change', handleDelegated);
    document.addEventListener('click', handleDelegated);

    return () => {
      delete (window as any).handleTaskToggle;
      delete (window as any).selectCommentText;
      delete (window as any).openCommentForText;
      document.removeEventListener('input', handleDelegated);
      document.removeEventListener('change', handleDelegated);
      document.removeEventListener('click', handleDelegated);
    };
  }, [content, onContentChange, isWysiwygMode]);

  // Restore caret position after content updates in WYSIWYG mode (only when
  // we re-rendered the DOM ourselves — i.e. an external content change, not
  // the user's own keystrokes).
  React.useEffect(() => {
    if (!isWysiwygMode || !isEditable) return;
    if (isUserTypingRef.current) {
      // User typed — DOM was not replaced, browser already manages the caret.
      isUserTypingRef.current = false;
      return;
    }
    const div = contentEditableRef.current;
    const sel = window.getSelection();
    if (!div || !sel || !savedSelectionRef.current) return;

    const { start, end } = savedSelectionRef.current;
    const range = document.createRange();

    // Walk text nodes to find positions
    let current = 0;
    const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT, null);
    let node = walker.nextNode() as Text | null;
    let startSet = false;
    let endSet = false;

    while (node) {
      const len = node.nodeValue ? node.nodeValue.length : 0;
      if (!startSet && current + len >= start) {
        range.setStart(node, Math.max(0, start - current));
        startSet = true;
      }
      if (!endSet && current + len >= end) {
        range.setEnd(node, Math.max(0, end - current));
        endSet = true;
        break;
      }
      current += len;
      node = walker.nextNode() as Text | null;
    }

    if (!startSet) {
      range.setStart(div, div.childNodes.length);
    }
    if (!endSet) {
      range.setEnd(div, div.childNodes.length);
    }

    sel.removeAllRanges();
    sel.addRange(range);
  }, [content, isWysiwygMode, isEditable]);

  // Imperatively sync the contentEditable's innerHTML only when content
  // changes from somewhere other than the user typing in the editor. This
  // avoids destroying the live DOM (and the user's caret/selection/spaces)
  // on every keystroke, which was causing spaces to disappear and unwanted
  // formatting changes when applying bold/italic/etc.
  React.useEffect(() => {
    if (!isWysiwygMode || !isEditable) return;
    const div = contentEditableRef.current;
    if (!div) return;
    if (isUserTypingRef.current) return; // user keystroke — DOM is already correct
    // Skip echo re-renders only when DOM is already populated. If the div is
    // empty (e.g. just switched into WYSIWYG mode), always render even if
    // content matches the last emitted markdown.
    if (lastEmittedMarkdownRef.current === content && div.innerHTML !== '') return;
    div.innerHTML = renderWysiwygContent(content);
  }, [content, isWysiwygMode, isEditable, comments, showAIHighlights]);

  const renderWysiwygContent = (text: string) => {
    // 1) Inject non-destructive comment tokens into the raw source first
    const startTokenFor = (id: string) => `[[__C_START_${id}__]]`;
    const endTokenFor = (id: string) => `[[__C_END_${id}__]]`;
    const escapeAttr = (s: string) =>
      (s || '')
        .replace(/&/g, '&amp;')
        .replace(/\"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    let tokenized = text;
    try {
      const sorted = [...comments].filter(c => !!c.selection).sort((a, b) =>
        (a.selection!.start - b.selection!.start)
      );
      let shift = 0;
      for (const c of sorted) {
        const id = String(c.id);
        const sel = c.selection!;
        const s = sel.start + shift;
        const e = sel.end + shift;
        if (s < 0 || e < s || s > tokenized.length) continue;
        const startToken = startTokenFor(id);
        const endToken = endTokenFor(id);
        tokenized = tokenized.slice(0, s) + startToken + tokenized.slice(s, e) + endToken + tokenized.slice(e);
        shift += startToken.length + endToken.length;
      }
    } catch {}

    // 2) Convert markdown/HTML to visual formatting with better multi-paragraph handling
    let processedText = tokenized;

    // Tokenize <ai-generated> tags BEFORE markdown processing so the inner
    // markdown (headings, blank lines, paragraphs) is still processed
    // line-by-line. Tokens are inert and survive all the regex passes; we
    // swap them for spans (or strip them) at the very end.
    const AI_START_TOKEN = '\u0001AIGEN_START\u0001';
    const AI_END_TOKEN = '\u0001AIGEN_END\u0001';
    processedText = processedText
      .replace(/<ai-generated>/g, AI_START_TOKEN)
      .replace(/<\/ai-generated>/g, AI_END_TOKEN);

    // Protect raw <ol>...</ol> / <ul>...</ul> blocks (incl. nested <li>) so
    // the per-line wrapper below doesn't shatter them into <div> per <li>,
    // which was breaking numbered lists into "different sentences" without
    // numbers when the AI returned raw HTML lists.
    const RAW_BLOCKS: string[] = [];
    processedText = processedText.replace(
      /<(ol|ul)\b[^>]*>[\s\S]*?<\/\1>/gi,
      (match) => {
        let styled = match;
        const openTag = styled.match(/^<[^>]*>/)?.[0] || '';
        if (/^<ol\b/i.test(openTag) && !/style=/i.test(openTag)) {
          styled = styled.replace(/^<ol\b/i, '<ol style="margin: 8px 0; padding-left: 20px; list-style-type: decimal;"');
        } else if (/^<ul\b/i.test(openTag) && !/style=/i.test(openTag)) {
          styled = styled.replace(/^<ul\b/i, '<ul style="margin: 8px 0; padding-left: 20px; list-style-type: disc;"');
        }
        const i = RAW_BLOCKS.push(styled) - 1;
        return `\u0002RAWBLK_${i}\u0002`;
      }
    );

    // First, handle multi-line formatting that needs to be processed line by line
    const lines = processedText.split('\n');
    let inList = false;
    let listType = '';
    
    const processedLines = lines.map((line, index) => {
      const trimmedLine = line.trim();

      // Pass raw list placeholders through unchanged.
      if (/^\u0002RAWBLK_\d+\u0002$/.test(trimmedLine)) {
        if (inList) {
          const closeTag = listType === 'ol' ? '</ol>' : '</ul>';
          inList = false;
          listType = '';
          return closeTag + trimmedLine;
        }
        return trimmedLine;
      }
      
      // Handle lists with proper wrapping
      if (trimmedLine.match(/^- (?!\[[ x]\] )/)) {
        if (!inList || listType !== 'ul') {
          if (inList) {
            // Close previous list
            const closeTag = listType === 'ol' ? '</ol>' : listType === 'task' ? '</ul>' : '</ul>';
            inList = true;
            listType = 'ul';
            return closeTag + '<ul style="margin: 8px 0; padding-left: 20px; list-style-type: disc;"><li style="margin: 2px 0;">' + trimmedLine.substring(2) + '</li>';
          } else {
            inList = true;
            listType = 'ul';
            return '<ul style="margin: 8px 0; padding-left: 20px; list-style-type: disc;"><li style="margin: 2px 0;">' + trimmedLine.substring(2) + '</li>';
          }
        }
        return '<li style="margin: 2px 0;">' + trimmedLine.substring(2) + '</li>';
      } else if (trimmedLine.match(/^\d+\. /)) {
        const listNumber = trimmedLine.match(/^(\d+)\. /)?.[1] || '1';
        const itemText = trimmedLine.replace(/^\d+\. /, '');
        if (!inList || listType !== 'ol') {
          if (inList) {
            // Close previous list
            const closeTag = listType === 'ul' ? '</ul>' : listType === 'task' ? '</ul>' : '</ul>';
            inList = true;
            listType = 'ol';
            return closeTag + `<ol style="margin: 8px 0; padding-left: 20px;" start="${listNumber}"><li style="margin: 2px 0;">${itemText}</li>`;
          } else {
            inList = true;
            listType = 'ol';
            return `<ol style="margin: 8px 0; padding-left: 20px;" start="${listNumber}"><li style="margin: 2px 0;">${itemText}</li>`;
          }
        }
        return `<li style="margin: 2px 0;">${itemText}</li>`;
      } else if (trimmedLine.match(/^- \[[ x]\] /)) {
        // Task lists
        const isChecked = trimmedLine.includes('[x]');
        const taskText = trimmedLine.replace(/^- \[[ x]\] /, '');
        
        if (!inList || listType !== 'task') {
          if (inList) {
            // Close previous list
            const closeTag = listType === 'ol' ? '</ol>' : '</ul>';
            inList = true;
            listType = 'task';
            return closeTag + `<ul style="margin: 8px 0; padding-left: 0; list-style: none;"><li style="margin: 2px 0;"><label style="display: flex; align-items: center; cursor: pointer;"><input type="checkbox" ${isChecked ? 'checked' : ''} data-task-text="${taskText.replace(/"/g, '&quot;')}" onclick="window.handleTaskToggle(this)" onchange="window.handleTaskToggle(this)" style="margin-right: 8px;"> <span${isChecked ? ' style="text-decoration: line-through; opacity: 0.6;"' : ''}>${taskText}</span></label></li>`;
          } else {
            inList = true;
            listType = 'task';
            return `<ul style="margin: 8px 0; padding-left: 0; list-style: none;"><li style="margin: 2px 0;"><label style="display: flex; align-items: center; cursor: pointer;"><input type="checkbox" ${isChecked ? 'checked' : ''} data-task-text="${taskText.replace(/"/g, '&quot;')}" onclick="window.handleTaskToggle(this)" onchange="window.handleTaskToggle(this)" style="margin-right: 8px;"> <span${isChecked ? ' style="text-decoration: line-through; opacity: 0.6;"' : ''}>${taskText}</span></label></li>`;
          }
        }
        return `<li style="margin: 2px 0;"><label style="display: flex; align-items: center; cursor: pointer;"><input type="checkbox" ${isChecked ? 'checked' : ''} data-task-text="${taskText.replace(/"/g, '&quot;')}" onchange="window.handleTaskToggle(this)" style="margin-right: 8px;"> <span${isChecked ? ' style="text-decoration: line-through; opacity: 0.6;"' : ''}>${taskText}</span></label></li>`;
      } else {
        // Close list if we were in one
        if (inList) {
          inList = false;
          const closeTag = listType === 'ol' ? '</ol>' : listType === 'ul' ? '</ul>' : '</ul>';
          listType = '';
          if (trimmedLine) {
            // Use `line` (not `trimmedLine`) so leading/trailing spaces are preserved.
            return closeTag + '<div style="margin: 4px 0;">' + line + '</div>';
          } else {
            return closeTag + '<br>';
          }
        }
        // Preserve blank lines as visible spacing so paragraphs separated by
        // an empty line in markdown/HTML keep their gap in WYSIWYG.
        return trimmedLine ? '<div style="margin: 4px 0;">' + line + '</div>' : '<div><br></div>';
      }
    });

    // Close any open list at the end
    if (inList) {
      const closeTag = listType === 'ol' ? '</ol>' : '</ul>';
      processedLines.push(closeTag);
    }

    processedText = processedLines.join('');

    // Now apply other formatting
    let html = processedText
      // HTML elements first (preserve existing HTML)
      .replace(/<h1>(.*?)<\/h1>/gs, '<h1 style="font-size: 2.25rem; font-weight: bold; margin: 16px 0;">$1</h1>')
      .replace(/<h2>(.*?)<\/h2>/gs, '<h2 style="font-size: 1.875rem; font-weight: bold; margin: 14px 0;">$1</h2>')
      .replace(/<h3>(.*?)<\/h3>/gs, '<h3 style="font-size: 1.5rem; font-weight: bold; margin: 12px 0;">$1</h3>')
      .replace(/<h4>(.*?)<\/h4>/gs, '<h4 style="font-size: 1.25rem; font-weight: bold; margin: 10px 0;">$1</h4>')
      .replace(/<h5>(.*?)<\/h5>/gs, '<h5 style="font-size: 1.125rem; font-weight: bold; margin: 8px 0;">$1</h5>')
      .replace(/<h6>(.*?)<\/h6>/gs, '<h6 style="font-size: 1rem; font-weight: bold; margin: 8px 0;">$1</h6>')
      
      // Markdown Headings (only if not already HTML)
      .replace(/^###### (.*$)/gm, '<h6 style="font-size: 1rem; font-weight: bold; margin: 8px 0;">$1</h6>')
      .replace(/^##### (.*$)/gm, '<h5 style="font-size: 1.125rem; font-weight: bold; margin: 8px 0;">$1</h5>')
      .replace(/^#### (.*$)/gm, '<h4 style="font-size: 1.25rem; font-weight: bold; margin: 10px 0;">$1</h4>')
      .replace(/^### (.*$)/gm, '<h3 style="font-size: 1.5rem; font-weight: bold; margin: 12px 0;">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 style="font-size: 1.875rem; font-weight: bold; margin: 14px 0;">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 style="font-size: 2.25rem; font-weight: bold; margin: 16px 0;">$1</h1>')
      
      // Blockquotes with multi-line support
      .replace(/<blockquote>"(.*?)"<\/blockquote>/gs, '<blockquote style="border-left: 4px solid #3b82f6; padding: 12px 16px; margin: 16px 0; color: #6b7280; font-style: italic; background: #f8fafc; border-radius: 4px;">"$1"</blockquote>')
      .replace(/<blockquote>(.*?)<\/blockquote>/gs, '<blockquote style="border-left: 4px solid #3b82f6; padding: 12px 16px; margin: 16px 0; color: #6b7280; font-style: italic; background: #f8fafc; border-radius: 4px;">$1</blockquote>')
      .replace(/^> (.*$)/gm, '<blockquote style="border-left: 4px solid #3b82f6; padding-left: 16px; margin: 16px 0; color: #6b7280; font-style: italic; background: #f8fafc; padding: 12px 16px; border-radius: 4px;">$1</blockquote>')
      
      // Text formatting (with multiline support using 's' flag)
      .replace(/\*\*(.*?)\*\*/gs, '<strong>$1</strong>')
      .replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/gs, '<em>$1</em>')
      .replace(/<u>(.*?)<\/u>/gs, '<u>$1</u>')
      .replace(/~~(.*?)~~/gs, '<del>$1</del>')
      
      // Color spans (multiline support)
      .replace(/<span style="color: ([^"]+);">(.*?)<\/span>/gs, '<span style="color: $1;">$2</span>')
      
      // Font styles (multiline support)
      .replace(/<font face="([^"]+)">(.*?)<\/font>/gs, '<span style="font-family: $1;">$2</span>')
      
      // Inline code
      .replace(/`([^`\n]+)`/g, '<code style="background: #f3f4f6; padding: 2px 4px; border-radius: 3px; font-family: monospace; font-size: 0.875em;">$1</code>')
      
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #3b82f6; text-decoration: underline;">$1</a>')
      
      // Line breaks (normalize spacing)
      .replace(/\n{3,}/g, '\n\n') // Normalize excessive newlines
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>');

    // Restore protected raw <ol>/<ul> blocks now that all line-based
    // processing is done.
    html = html.replace(/\u0002RAWBLK_(\d+)\u0002/g, (_, i) => RAW_BLOCKS[Number(i)] ?? '');

    // 3) Replace tokens with actual highlight spans (post-formatting)
    // Remove any remaining comment tokens that weren't properly matched
    html = html.replace(/\[\[__C_START_\d+__\]\]/g, '');
    html = html.replace(/\[\[__C_END_\d+__\]\]/g, '');
    
    try {
      const sorted = [...comments].filter(c => !!c.selection);
      for (const c of sorted) {
        const id = String(c.id);
        const startToken = startTokenFor(id);
        const endToken = endTokenFor(id);
        const title = escapeAttr(`💬 Comment: ${c.text}`);
        html = html.split(startToken).join(`<span style="background-color: rgba(250, 204, 21, 0.2); border-bottom: 2px solid rgba(250, 204, 21, 0.5); cursor: pointer; position: relative;" title="${title}" data-comment-id="${id}" data-selection-start="${c.selection.start}" data-selection-end="${c.selection.end}" onclick="window.selectCommentText && window.selectCommentText(${c.selection.start}, ${c.selection.end}); window.openCommentForText && window.openCommentForText(${c.selection.start}, ${c.selection.end}, '${id}')">`);
        html = html.split(endToken).join('</span>');
      }
    } catch {}

    // 4) Swap AI-generated tokens for highlight spans (or strip them).
    // Use a block-level wrapper so multi-paragraph AI content keeps its
    // vertical spacing instead of collapsing into one inline run.
    if (showAIHighlights) {
      html = html
        .split(AI_START_TOKEN)
        .join('<span class="ai-generated-block bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-1 rounded-sm border-l-2 border-blue-400 dark:border-blue-500" style="display:inline;">')
        .split(AI_END_TOKEN)
        .join('</span>');
    } else {
      html = html.split(AI_START_TOKEN).join('').split(AI_END_TOKEN).join('');
    }

    return html;
  };

  return (
    <div>
      <div className="relative mb-4">
        <img
          src="/lovable-uploads/f10795cf-dc59-4f21-912e-0e05da31e0ed.png"
          alt="MindNote Logo"
          className="note-brand-logo mx-auto h-5 w-auto z-10 opacity-70 dark:hidden"
        />
        <img
          src={mindnoteBotLogoWhite}
          alt="MindNote Logo"
          className="note-brand-logo mx-auto h-5 w-auto z-10 opacity-70 hidden dark:block"
        />
        <div className="flex justify-between items-center mb-4 mt-4">
          <Input
            placeholder="Note title..."
            value={title}
            onChange={handleTitleChange}
            readOnly={!isEditable}
            className="text-lg font-semibold bg-transparent border-none focus:ring-0"
            data-tour="note-title"
          />
        </div>
        {isEditable && (title.trim() || content.trim()) && (
          <div className="text-xs text-green-600 mb-4 px-1">
            To save your note, please fill both title and content.
          </div>
        )}
      </div>
      
      {isEditable ? (
        isWysiwygMode ? (
          <BlockNoteEditorWrapper
            content={content}
            externalRevision={externalRevision}
            onContentChange={onContentChange}
            backgroundColor={backgroundColor}
            editable={true}
            onElementRef={(el) => {
              // IMPORTANT: do NOT assign to contentEditableRef — the legacy
              // WYSIWYG effects below imperatively rewrite that node's
              // innerHTML, which would destroy the ProseMirror DOM and make
              // BlockNote uneditable. Only expose to the parent toolbar.
              onGetCurrentElement?.(el);
            }}
            onTextSelection={handleTextSelection}
          />
        ) : (
          <Textarea
            ref={textareaRef}
            placeholder="Start writing..."
            value={
              showAIHighlights
                ? content
                : content.replace(/<\/?ai-generated>/g, '')
            }
            onChange={handleContentChange}
            onMouseUp={handleTextSelection}
            onTouchEnd={handleTextSelection}
            onSelect={handleTextSelection}
            className="min-h-[200px] resize-none border-none focus:ring-0 font-mono bg-transparent"
            style={{ 
              backgroundColor: backgroundColor,
            }}
            data-tour="note-content"
            spellCheck="false"
          />
        )
      ) : (
        isWysiwygMode ? (
          <BlockNoteEditorWrapper
            content={content}
            externalRevision={externalRevision}
            backgroundColor={backgroundColor}
            editable={false}
          />
        ) : (
          <div 
            className="min-h-[200px] whitespace-pre-wrap break-words font-mono"
            dangerouslySetInnerHTML={{ __html: renderHighlightedContent() }}
          />
        )
      )}

    </div>
  );
};

export default NoteContent;
